/**
 * Explains and configures ServiceNow's incremental synchronization cadence.
 *
 * The backend owns sys_id revision checkpoints. This view exposes that
 * behavior beside the shared product schedule so administrators understand
 * that scheduled runs do not re-import unchanged records.
 */
import { useState } from 'react'
import { ChevronRight } from 'lucide-react'

import { ConnectorScheduleField } from '../../components/ConnectorScheduleSelect'
import type { ConnectorSchedule } from '../../types'
import { cn } from '@/lib/utils'

export function ServiceNowSyncOptions({
  schedule,
  onScheduleChange,
  disabled = false,
}: {
  schedule: ConnectorSchedule
  onScheduleChange: (schedule: ConnectorSchedule) => void
  disabled?: boolean
}) {
  const [showExplanation, setShowExplanation] = useState(false)

  return (
    <div className="space-y-3">
      <div className="[&>div]:flex [&>div]:items-center [&>div]:gap-3 [&>div]:space-y-0 [&_[data-slot=select-trigger]]:h-8 [&_[data-slot=select-trigger]]:w-[218px] [&_p]:hidden">
        <ConnectorScheduleField
          value={schedule}
          onChange={onScheduleChange}
          disabled={disabled}
          ariaLabel="ServiceNow connector schedule"
        />
      </div>

      <button
        type="button"
        className="flex items-center gap-1 rounded-sm text-xs font-medium text-slate-600 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
        aria-expanded={showExplanation}
        onClick={() => setShowExplanation(current => !current)}
      >
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 transition-transform duration-150',
            showExplanation && 'rotate-90',
          )}
        />
        How syncing works
      </button>

      {showExplanation && (
        <div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/70 p-3 text-xs leading-4 text-slate-600">
          <p>
            Each scheduled run transfers only new or changed sys_id revisions inside the record window — unchanged records are never re-imported.
          </p>
          <p>
            Historical records are downloaded and token-counted for free before AI runs. After you continue once, scheduled new or changed records run through the configured review workflow automatically.
          </p>
        </div>
      )}
    </div>
  )
}
